/**
 * Contenuto dell'informativa privacy, portato dalla v1
 * (`enermed-web-app/src/pages/login/components/privacyContent.js`).
 *
 * Differenze rispetto alla v1:
 *  - rimossi i wrapper `<!DOCTYPE>/<html>/<body>` e gli `<style>` inline:
 *    qui il markup viene sanitizzato con DOMPurify e stilizzato via la classe
 *    `.news-content` (src/index.css), lo stesso meccanismo delle news;
 *  - corretto il refuso "Perido" → "Periodo";
 *  - AGGIUNTA la sezione su Cookie e Google Analytics 4 (introdotti nella v2),
 *    con finalità, base giuridica (consenso) e modalità di revoca.
 *
 * Il render avviene con dangerouslySetInnerHTML DOPO sanitizzazione: vedi
 * PrivacyPage.tsx. Non inserire qui script o attributi inline: verrebbero
 * comunque rimossi dal sanitizer.
 */

export const PRIVACY_LAST_UPDATED = '15 giugno 2026'

export const privacyHtml = `
  <h2>Privacy Policy di Enermed App e WebApp</h2>
  <p>
    Lo scopo del presente documento (di seguito “Privacy Policy”) è di informare gli Utenti sul trattamento dei Dati
    Personali acquisiti mediante l’utilizzo di Enermed App e WebApp.
    Il Titolare del Trattamento, come successivamente identificato, potrà modificare o semplicemente aggiornare, in
    tutto o in parte, la presente Privacy Policy dandone informazione agli Utenti.
    Le modifiche e gli aggiornamenti saranno vincolanti non appena pubblicati sull’applicazione.
    L’Utente è pertanto invitato a leggere la Privacy Policy ad ogni accesso all’Applicazione.
    Nel caso di mancata accettazione delle modifiche apportate alla presente Privacy Policy, l’Utente è tenuto a
    cessare l’utilizzo dell’applicazione e potrà richiedere al Titolare di rimuovere i propri Dati Personali.
  </p>

  <h3>1. Titolare del Trattamento dei Dati</h3>
  <p>
    Ai sensi dell’art. 4 del Regolamento (UE) 2016/679, Titolare del Trattamento dei dati è la Società Enermed Srl,
    P. Iva 03111940924, via Dante 93, 09128 Cagliari (CA) ITA.
  </p>
  <p><strong>Indirizzo email del Titolare: amministrazione@enermed.it.</strong></p>

  <h3>2. Tipologie di Dati raccolti</h3>
  <p>Il Titolare raccoglie le seguenti tipologie di dati personali:</p>
  <h4>1. Dati personali forniti direttamente dall’Utente:</h4>
  <ul>
    <li>Dati anagrafici e di contatto: (nome, cognome, indirizzo, numero di telefono, etc.);</li>
  </ul>
  <h4>2. Dati acquisiti dal Titolare in maniera automatizzata:</h4>
  <ul>
    <li>
      Dati tecnici: dati personali prodotti dai dispositivi, dalle applicazioni, dagli strumenti e dai protocolli
      utilizzati, quali ad esempio, informazioni sul dispositivo utilizzato, indirizzo IP, tipo di browser, tipo
      di provider internet (ISP).
    </li>
    <li>
      Dati di navigazione e utilizzo dell’app e webapp quali, ad esempio, pagine visitate, numero di click, azioni
      compiute, durata delle sessioni, etc.
    </li>
    <li>
      Dati relativi alla posizione dell’Utente quali, ad esempio, dati di geolocalizzazione che identificano la
      sua posizione e che possono essere raccolti tramite rete satellitare (es. GPS) e altri mezzi.
    </li>
    <li>
      Tali dati sono raccolti previo consenso dell’Utente interessato, il quale, potrà revocarlo in ogni momento.
    </li>
  </ul>
  <p>
    Se non diversamente specificato, tutti i Dati richiesti da questa Applicazione sono obbligatori.
    Se l’Utente rifiutasse di comunicarli, potrebbe essere impossibile per questa Applicazione fornire il Servizio.
    Nei casi in cui questa Applicazione indichi alcuni Dati come facoltativi, gli Utenti sono liberi di astenersi
    dal comunicare tali Dati, senza che ciò abbia alcuna conseguenza sulla disponibilità del Servizio o sulla sua
    operatività.
    L’eventuale utilizzo di Cookie - o di altri strumenti di tracciamento - da parte di questa Applicazione o dei
    titolari dei servizi terzi utilizzati da questa Applicazione, ove non diversamente precisato, ha la finalità di
    fornire il Servizio richiesto dall'Utente, oltre alle ulteriori finalità descritte nel presente documento e
    nella sezione "Cookie e statistiche di utilizzo".
    L'Utente si assume la responsabilità dei Dati Personali di terzi ottenuti, pubblicati o condivisi mediante
    questa Applicazione e garantisce di avere il diritto di comunicarli o diffonderli, liberando il Titolare da
    qualsiasi responsabilità verso terzi.
  </p>

  <h3>3. Modalità e luogo del trattamento dei Dati raccolti</h3>
  <h4>1) Modalità di trattamento</h4>
  <p>
    Il Titolare adotta le opportune misure di sicurezza volte ad impedire l’accesso, la divulgazione, la modifica o
    la distruzione non autorizzate dei Dati Personali.
    Il trattamento viene effettuato mediante strumenti informatici e/o telematici, con modalità organizzative e con
    logiche strettamente correlate alle finalità indicate.
    Oltre al Titolare, in alcuni casi, potrebbero avere accesso ai Dati altri soggetti coinvolti nell’organizzazione
    di questa Applicazione (personale amministrativo, commerciale, marketing, legali, amministratori di sistema)
    ovvero soggetti esterni (come fornitori di servizi tecnici terzi, corrieri postali, hosting provider, società
    informatiche, agenzie di comunicazione) nominati anche, se necessario, Responsabili del Trattamento da parte del
    Titolare.
    L’elenco aggiornato dei Responsabili potrà sempre essere richiesto al Titolare del Trattamento.
  </p>
  <h4>2) Luogo</h4>
  <p>
    I Dati sono trattati presso le sedi operative del Titolare ed in ogni altro luogo in cui le parti coinvolte nel
    trattamento siano localizzate.
    I suoi dati personali potranno essere trasferiti in paesi extra-UE, in particolare nel caso di servizi che siano
    ubicati al di fuori del territorio dell’Unione Europea, come ad esempio il servizio di cloud storage.
    Qualora risulti necessario per la gestione del Suo rapporto di lavoro trasferire i Suoi dati al di fuori dello
    Spazio Economico Europeo, ai soggetti destinatari dei dati verranno imposti obblighi di protezione e sicurezza
    equivalenti a quelli garantiti dal Titolare.
    In ogni caso saranno comunicati i soli dati necessari al perseguimento degli scopi previsti e descritti e
    saranno applicate, ove richiesto, le garanzie previste per i trasferimenti dei dati verso paesi terzi.
    L’Utente può verificare se abbia luogo uno dei trasferimenti appena descritti esaminando la sezione di questo
    documento relativa ai dettagli sul trattamento di Dati Personali o chiedere informazioni al Titolare
    contattandolo agli estremi riportati in apertura.
  </p>

  <h3>4. Periodo di conservazione</h3>
  <p>
    I Dati sono trattati e conservati per il tempo richiesto dalle finalità per le quali sono stati raccolti.
    Pertanto:
  </p>
  <ul>
    <li>
      I Dati Personali raccolti per scopi collegati all’esecuzione di un contratto tra il Titolare e l’Utente
      saranno trattenuti per cinque anni da quando sia completata l’esecuzione di tale contratto.
    </li>
    <li>
      I Dati Personali raccolti per finalità riconducibili all’interesse legittimo del Titolare saranno trattenuti
      sino al soddisfacimento di tale interesse.
      L’Utente può ottenere ulteriori informazioni in merito all’interesse legittimo perseguito dal Titolare nelle
      relative sezioni di questo documento o contattando il Titolare.
    </li>
  </ul>
  <p>
    Quando il trattamento è basato sul consenso dell’Utente, il Titolare può conservare i Dati Personali più a lungo
    sino a quando detto consenso non venga revocato.
    Inoltre, il Titolare potrebbe essere obbligato a conservare i Dati Personali per un periodo più lungo in
    ottemperanza ad un obbligo di legge o per ordine di un’autorità.
    Al termine del periodo di conservazione i Dati Personali saranno cancellati.
    Pertanto, allo spirare di tale termine il diritto di accesso, cancellazione, rettifica ed il diritto alla
    portabilità dei Dati non potranno più essere esercitati.
  </p>

  <h3>5. Finalità e base giuridica del Trattamento</h3>
  <p>I Dati dell’Utente sono raccolti dal Titolare per le seguenti finalità:</p>
  <p><strong>1. Per l’esecuzione del contratto con l’Utente e precisamente:</strong></p>
  <ul>
    <li>Adempimento di ogni obbligo derivante dal rapporto precontrattuale o contrattuale con l’Utente;</li>
    <li>
      Registrazione ed autenticazione dell’Utente: per consentire all’Utente di registrarsi sull’app e webapp,
      accedere ed essere identificato. A seconda di quanto indicato di seguito, i servizi di registrazione e
      di autenticazione potrebbero essere forniti con l’ausilio di terze parti. Qualora questo avvenga, questa
      applicazione potrà accedere ad alcuni Dati conservati dal servizio terzo usato per la registrazione o
      l’identificazione.
    </li>
    <li>Supporto e contatto con l’Utente: per rispondere alle sue specifiche richieste.</li>
    <li>
      Questo tipo di servizi consente di gestire un database di contatti e-mail, contatti telefonici o
      contatti di qualunque altro tipo, utilizzati per comunicare con l’Utente.
    </li>
    <li>
      Questi servizi potrebbero inoltre consentire di raccogliere dati relativi alla data e all’ora di
      visualizzazione dei messaggi da parte dell’Utente, così come all’interazione dell'Utente con essi, come
      le informazioni sui click sui collegamenti inseriti nei messaggi.
    </li>
    <li>
      Gestione dei pagamenti: per gestire pagamenti effettuati dall’Utente tramite carta di credito (anche
      mediante circuito Stripe), bonifico bancario e altri strumenti.
    </li>
  </ul>
  <p><strong>2. Per obbligo di legge e precisamente:</strong></p>
  <ul>
    <li>
      L’adempimento di un qualsiasi obbligo previsto dalle vigenti normative, leggi e regolamenti, in
      particolare in materia tributaria e fiscale.
    </li>
  </ul>
  <p><strong>3. Sulla base del consenso espresso dell’Utente, per:</strong></p>
  <ul>
    <li>
      Finalità di marketing via e-mail di servizi offerti dal Titolare: per vendere direttamente i servizi del
      Titolare utilizzando l’indirizzo e-mail fornito dall’Utente;
    </li>
    <li>
      Statistiche di utilizzo del sito tramite cookie analitici (vedi sezione "Cookie e statistiche di utilizzo").
    </li>
  </ul>
  <p><strong>4. Sulla base del legittimo interesse del Titolare:</strong></p>
  <ul>
    <li>
      Gestione, ottimizzazione e monitoraggio dell’infrastruttura tecnica: per identificare e risolvere
      eventuali problemi tecnici, per migliorare le performance dell’App e Webapp, per gestire e organizzare
      le informazioni in un sistema informatico (es. server, database, etc.).
    </li>
    <li>
      Difesa dei propri diritti nel caso di instaurazione di un eventuale contenzioso giudiziale, nonché
      finalizzato alla tutela e recupero dei crediti, direttamente o attraverso soggetti
      (avvocati/agenzie/società di recupero credito).
    </li>
  </ul>
  <p>
    Il trattamento effettuato per le finalità di cui al comma 1 avverrà ai sensi della condizione di liceità di cui
    all’articolo 6, paragrafo 1, lettera b), GDPR, essendo il trattamento necessario all’esecuzione del contratto di
    fornitura di cui Lei è parte.
    Il trattamento effettuato ai sensi del comma 2 sarà fondato sulla condizione di liceità di cui all’articolo 6,
    paragrafo 1, lettera c), GDPR, ossia dalla necessità di adempiere ad un obbligo legale a cui è soggetto il
    titolare del trattamento.
    Il trattamento effettuato ai sensi del comma 3 avverrà sulla base del consenso espresso dell’Interessato, in
    conformità con quanto disposto all’articolo 6, paragrafo 1, lettera a) GDPR.
    Il trattamento effettuato ai sensi del comma 4 avverrà sulla base del perseguimento del legittimo interesse del
    Titolare, in conformità con quanto disposto all’articolo 6, paragrafo 1, lettera f) GDPR.
  </p>

  <h3>6. Notifiche Push</h3>
  <p>
    Questa Applicazione può inviare notifiche push all'Utente nel rispetto delle finalità previste nella presente
    privacy policy.
    Nella maggior parte dei casi, gli Utenti possono scegliere di non ricevere notifiche push consultando le
    impostazioni del proprio dispositivo, come le impostazioni di notifica per i telefoni cellulari.
    L’Utente interessato dal trattamento potrà, quindi, modificare tali impostazioni per questa, per alcune o per
    tutte le applicazioni presenti sul dispositivo utilizzato.
    Gli Utenti devono essere consapevoli che la disabilitazione delle notifiche push può influire negativamente
    sull'utilizzo di questa Applicazione.
  </p>

  <h3>7. Cookie e statistiche di utilizzo</h3>
  <p>
    Questa Applicazione utilizza cookie tecnici necessari al proprio funzionamento (ad esempio per la sessione di
    accesso e la sicurezza), per i quali non è richiesto il consenso dell’Utente.
  </p>
  <p>
    Previo consenso dell’Utente, questa Applicazione utilizza inoltre <strong>cookie analitici</strong> tramite il
    servizio <strong>Google Analytics 4</strong>, fornito da Google Ireland Limited. Tali cookie consentono di
    raccogliere informazioni statistiche e aggregate sull’utilizzo del sito (ad esempio pagine visitate e durata
    delle sessioni), al fine di comprenderne l’utilizzo e migliorarne i contenuti e le funzionalità.
  </p>
  <ul>
    <li>
      <strong>Base giuridica:</strong> consenso dell’Utente (art. 6, par. 1, lett. a) GDPR), raccolto tramite
      l’apposito banner al primo accesso.
    </li>
    <li>
      <strong>Gestione del consenso:</strong> il servizio è attivato secondo la modalità “Consent Mode” di Google.
      Finché l’Utente non presta il consenso, i cookie analitici non vengono installati e nessun dato di analisi
      viene trasmesso.
    </li>
    <li>
      <strong>Revoca:</strong> l’Utente può modificare o revocare in qualsiasi momento le proprie preferenze
      cancellando i cookie del sito dalle impostazioni del browser; al successivo accesso verrà nuovamente proposto
      il banner di consenso.
    </li>
    <li>
      <strong>Luogo del trattamento:</strong> Irlanda. Per maggiori informazioni si rinvia alla privacy policy di
      Google.
    </li>
  </ul>

  <h3>8. Ulteriori servizi utilizzati nell’app e webapp</h3>
  <p>
    Il Titolare del Trattamento informa l’Utente interessato che i suoi dati personali, raccolti mediante l’utilizzo
    dell’app e webapp potranno essere utilizzati per l’erogazione dei servizi di seguito specificati.
    In tali ipotesi il trattamento dei dati personali avverrà in territorio diverso da quello Nazionale.
    In ogni caso il Titolare garantisce all’Utente che il trattamento dei suoi dati avverrà nel rispetto delle
    finalità indicate nella presente privacy, nonché nel rispetto di adeguate misure di controllo e di sicurezza.
  </p>
  <ul>
    <li>
      <strong>Notifiche di Firebase (Google Ireland Limited).</strong>
      Firebase Notifications è un servizio di messaggistica fornito da Google Ireland Limited.
      Firebase Notifications può essere integrato con Firebase Analytics per inviare messaggi ad Utenti sulla base
      dei risultati dell’attività di analisi e per tracciare eventi di utilizzo e conversione da parte degli Utenti.
      Dati Personali raccolti: varie tipologie di Dati secondo quanto specificato dalla privacy policy del servizio.
      Luogo del trattamento: Irlanda.
    </li>
    <li>
      <strong>Firebase Cloud Messaging (Google Ireland Limited).</strong>
      Firebase Cloud Messaging è un servizio di messaggistica fornito da Google Ireland Limited.
      Permette al Titolare di inviare messaggi e notifiche ad Utenti su piattaforme quali Android, iOS e sul web.
      Dati Personali raccolti: varie tipologie di Dati secondo quanto specificato dalla privacy policy del servizio.
      Luogo del trattamento: Irlanda.
    </li>
    <li>
      <strong>Hosting ed infrastruttura backend.</strong>
      Questo tipo di servizi ha la funzione di ospitare Dati e file che permettono a questa Applicazione di
      funzionare, ne consentono la distribuzione e mettono a disposizione un'infrastruttura pronta all'uso per
      erogare specifiche funzionalità di questa Applicazione.
    </li>
    <li>
      <strong>Amazon Web Services (AWS) (Amazon Web Services, Inc.).</strong>
      Servizio di hosting e backend fornito da Amazon Web Services, Inc.
      Dati Personali raccolti: varie tipologie di Dati secondo quanto specificato dalla privacy policy del servizio.
      Luogo del trattamento: Irlanda.
    </li>
    <li>
      <strong>Login with Amazon (Amazon).</strong>
      Servizio di registrazione ed autenticazione fornito da Amazon.com, Inc.
    </li>
  </ul>

  <h3>9. Diritti dell'Utente</h3>
  <p>
    Gli Utenti possono esercitare determinati diritti con riferimento ai Dati trattati dal Titolare.
    In particolare, l’Utente ha il diritto di:
  </p>
  <ul>
    <li>
      <strong>revocare il consenso in ogni momento.</strong> L’Utente può revocare il consenso al trattamento dei
      propri Dati Personali precedentemente espresso.
    </li>
    <li>
      <strong>opporsi al trattamento dei propri Dati.</strong> L’Utente può opporsi al trattamento dei propri Dati
      quando esso avviene su una base giuridica diversa dal consenso.
    </li>
    <li>
      <strong>accedere ai propri Dati.</strong> L’Utente ha diritto ad ottenere informazioni sui Dati trattati dal
      Titolare, su determinati aspetti del trattamento ed a ricevere una copia dei Dati trattati.
    </li>
    <li>
      <strong>verificare e chiedere la rettifica.</strong> L’Utente può verificare la correttezza dei propri Dati e
      richiederne l’aggiornamento o la correzione.
    </li>
    <li>
      <strong>ottenere la limitazione del trattamento.</strong> Quando ricorrono determinate condizioni, l’Utente
      può richiedere la limitazione del trattamento dei propri Dati. In tal caso il Titolare non tratterà i Dati per
      alcun altro scopo se non la loro conservazione.
    </li>
    <li>
      <strong>ottenere la cancellazione o rimozione dei propri Dati Personali.</strong> Quando ricorrono determinate
      condizioni, l’Utente può richiedere la cancellazione dei propri Dati da parte del Titolare.
    </li>
    <li>
      <strong>ricevere i propri Dati o farli trasferire ad altro titolare.</strong> L'Utente ha diritto di ricevere
      i propri Dati in formato strutturato, di uso comune e leggibile da dispositivo automatico e, ove tecnicamente
      fattibile, di ottenerne il trasferimento senza ostacoli ad un altro titolare.
    </li>
    <li>
      <strong>proporre reclamo.</strong> L’Utente può proporre un reclamo all’autorità di controllo della protezione
      dei dati personali competente o agire in sede giudiziale.
    </li>
  </ul>
  <p>
    <strong>Come esercitare i diritti.</strong> Per esercitare i diritti dell’Utente, gli Utenti possono indirizzare
    una richiesta all’indirizzo amministrazione@enermed.it. Le richieste sono depositate a titolo gratuito ed evase
    dal Titolare nel più breve tempo possibile, in ogni caso entro un mese.
  </p>

  <h3>10. Ulteriori informazioni sul trattamento</h3>
  <ul>
    <li>
      <strong>Informative specifiche.</strong> Su richiesta dell’Utente, in aggiunta alle informazioni contenute in
      questa privacy policy, questa Applicazione potrebbe fornire all'Utente delle informative aggiuntive e
      contestuali riguardanti Servizi specifici, o la raccolta ed il trattamento di Dati Personali.
    </li>
    <li>
      <strong>Log di sistema e manutenzione.</strong> Per necessità legate al funzionamento ed alla manutenzione,
      questa Applicazione e gli eventuali servizi terzi da essa utilizzati potrebbero raccogliere log di sistema,
      ossia file che registrano le interazioni e che possono contenere anche Dati Personali, quali l’indirizzo IP
      Utente.
    </li>
    <li>
      <strong>Informazioni non contenute in questa policy.</strong> Ulteriori informazioni in relazione al
      trattamento dei Dati Personali potranno essere richieste in qualsiasi momento al Titolare del Trattamento
      utilizzando gli estremi di contatto.
    </li>
    <li>
      <strong>Modifiche a questa privacy policy.</strong> Il Titolare del Trattamento si riserva il diritto di
      apportare modifiche alla presente privacy policy in qualunque momento dandone informazione agli Utenti su
      questa pagina. Si prega dunque di consultare regolarmente questa pagina, facendo riferimento alla data di
      ultima modifica indicata in fondo. Qualora le modifiche interessino trattamenti la cui base giuridica è il
      consenso, il Titolare provvederà a raccogliere nuovamente il consenso dell’Utente, se necessario.
    </li>
  </ul>
`
